import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "@/lib/content";
import { MaskLine, Overline } from "@/components/site/Reveal";
import { ArrowDown } from "lucide-react";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen overflow-hidden pt-32 pb-16"
    >
      {/* Parallax abstract texture */}
      <motion.div
        style={{ y: bgY }}
        className="pointer-events-none absolute -right-40 -top-20 h-[70vh] w-[70vh] rounded-full opacity-40 blur-2xl"
      >
        <img
          src={ASSETS.sandTexture}
          alt=""
          className="h-full w-full object-cover rounded-full"
        />
      </motion.div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
        {/* Copy */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
          >
            <Overline>Behavioral Science · Leadership · Transformation</Overline>
          </motion.div>

          <h1 className="mt-6 font-serif font-light leading-[1.02] tracking-tight text-5xl sm:text-6xl lg:text-7xl">
            <MaskLine delay={0.25}>Applying behavioral</MaskLine>
            <MaskLine delay={0.38}>science to leadership,</MaskLine>
            <MaskLine delay={0.51}>
              learning &amp; <span className="italic text-primary">family life.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground"
          >
            Partnering with organizations, professionals, and families through
            coaching, strategy, and professional development grounded in
            behavioral science.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#paths"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium text-primary-foreground transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(193,119,103,0.6)]"
            >
              Learn About Services
            </a>
          </motion.div>
        </motion.div>

        {/* Portrait in arch */}
        <div className="lg:col-span-5 relative">
          <div className="relative mx-auto max-w-sm">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.3, delay: 0.4, ease: [0.76, 0, 0.24, 1] }}
              className="arch-top overflow-hidden"
            >
              <motion.div style={{ y: portraitY }}>
                <img
                  src={ASSETS.heroPortrait}
                  alt="Dr. Joanne Hill Powell"
                  className="w-full object-cover aspect-[3/4]"
                />
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="absolute bottom-5 left-5 backdrop-blur-xl bg-background/85 border border-border rounded-2xl px-6 py-4 shadow-[0_20px_40px_-15px_rgba(44,42,38,0.2)]"
            >
              <p className="font-serif text-lg leading-none">PhD · BCBA-D</p>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
                Columbia University
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.a
        href="#about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-muted-foreground"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce" strokeWidth={1.5} />
      </motion.a>
    </section>
  );
}
